import jsPDF from 'jspdf';
import 'jspdf-autotable';
import fallbackLogoSvg from './assets/clearfxai-logo.svg';
import * as utils from './utils.js';
import i18n from './i18n/i18n.js';
import { useExecutionStore } from './store/index';
import { formatReportQuoteStatus } from './components/reports/reportHelpers.js';

export const MARGIN = 3;
export const HEADER_HEIGHT = 32;
export const FONT_SIZE_CELL = 6.5;
export const FONT_SIZE_HEAD = 6.5;
export const REPORTS_GAP = 10;

// Converts an SVG URL to a base64 PNG data-URL for use with jsPDF.addImage.
// The fetch/text steps are awaitable; the Image onload step must remain a Promise
// because the browser Image API is callback-only.
export async function convertSvgToBase64(svgUrl) {
  const response = await fetch(svgUrl);
  const svgText = await response.text();
  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
}

// Converts a Blob (from an API response) to a base64 data-URL.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Fetches a broker logo by ID and returns it as a base64 data-URL, or null on failure.
async function fetchLogoBase64(brokerId) {
  if (!brokerId) return null;
  try {
    const res = await useExecutionStore().getBrokerLogo(brokerId);
    if (!res?.data) return null;
    return blobToBase64(res.data);
  } catch {
    return null;
  }
}

// Resolves the correct logo for the current user:
//   - Partners: try master-partner logo first, then own broker logo.
//   - Brokers and PRO users: try their associated broker logo.
//   - Fallback: bundled SVG asset.
export async function loadLogoForPDF() {
  const role = localStorage.getItem('userRole');
  const brokerId = localStorage.getItem('brokerId');
  // masterBroker is set by ToolBar.checkIsClientPartner via getBrokerById
  const masterBrokerId = localStorage.getItem('masterBroker');

  if (role === 'partner') {
    const masterLogo = await fetchLogoBase64(masterBrokerId);
    if (masterLogo) return masterLogo;
    const ownLogo = await fetchLogoBase64(brokerId);
    if (ownLogo) return ownLogo;
  } else if (role === 'broker' || role === 'pro') {
    const ownLogo = await fetchLogoBase64(brokerId);
    if (ownLogo) return ownLogo;
  }

  return convertSvgToBase64(fallbackLogoSvg);
}

export function formatTimestamp(now = new Date(), locale = 'en') {
  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: 'numeric',
    hour12: true,
  };
  const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const formattedTime = now.toLocaleTimeString(locale, timeOptions);
  return `${formattedDate}, ${formattedTime}`;
}

// Returns a stateless header-drawing function. The caller is responsible for
// deduplication (i.e. only calling it when the page has actually changed).
export function buildAddHeader(doc, logoImgData) {
  const pageWidth = doc.internal.pageSize.width;
  return function addHeader() {
    doc.setFillColor(41, 129, 186);
    doc.rect(MARGIN, 5, pageWidth - 2 * MARGIN, 8, 'F');
    if (logoImgData) {
      doc.addImage(logoImgData, 'PNG', MARGIN + 2, 6, 24, 5);
    }
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, 27, pageWidth - MARGIN, 27);
  };
}

export function addPageNumbersAndTimestamp(doc, formattedNow) {
  const totalPages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Timestamp inside the blue banner
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    const textWidth = doc.getTextWidth(formattedNow);
    doc.text(formattedNow, pageWidth - MARGIN - 2 - textWidth, 10.5);

    // Report title with page count (e.g. "Report - 1/2")
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(7, 7, 7);
    if (totalPages === 1) {
      doc.text(`${i18n.t('pdf.title')}`, MARGIN, 24);
    } else {
      doc.text(`${i18n.t('pdf.title')} - ${i}/${totalPages}`, MARGIN, 24);
    }
  }

  // Restore defaults
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
}

export function formatOperationTypeTitle(type) {
  const operationTypes = i18n.t('broker.operationTypes');
  return String(operationTypes?.[type] || type || '-');
}

// Converts a declarative columns array into the three separate arguments autoTable needs:
//   head - array of header strings
//   body - array of data values for a single row
//   columnStyles - object keyed by column index with jspdf-autotable style overrides
// One column should be intentionally left without a cellWidth so autoTable 
// stretches it to fill the available body width.
export function columnsToAutoTableArgs(columns) {
  const head = columns.map(c => c.header);
  const body = columns.map(c => c.data);
  const columnStyles = {};
  columns.forEach((c, idx) => {
    if (c.cellStyle) columnStyles[idx] = c.cellStyle;
  });
  return { head, body, columnStyles };
}

// Builds the quotes sub-table column definitions. Called once with an empty object to
// extract headers, then once per quote row to extract body data.
export function buildQuotesColumns(rfq, role, item = {}) {
  const isBroker = role === 'broker';
  const isPro = role === 'pro';
  const hasPartnerBroker = item.partner != null;

  // RFQ report rows carry the RFQ's status as `rfq_status`; operation report rows
  // carry their linked RFQ's as `status`.
  const rfqStatus = item.rfq_status ?? item.status;

  const cols = [
    {
      header: i18n.t('quoteInfo.status'),
      data: formatReportQuoteStatus(rfq, rfqStatus),
    },
    { header: i18n.t('quoteInfo.bankName'), data: rfq.bank_label },
  ];

  if (!isPro) {
    cols.push({
      header: i18n.t('quoteInfo.bankRate'),
      data: rfq.quote_bank_rate != null ? Number(rfq.quote_bank_rate).toFixed(4) : '-'
    });
  }

  cols.push({
      header: isBroker ? i18n.t('quoteInfo.brokerMarkup') : i18n.t('quoteInfo.spread'),
      data: isBroker ? rfq.broker_markup_str : (role === 'partner' ? rfq.spread_avg_spot : '-'),
  });

  if (isBroker && hasPartnerBroker) {
    cols.push({
      header: i18n.t('quoteInfo.partnerSpread'),
      data: rfq.partner_markup_str || '-',
    });
  }

  cols.push(
    { header: i18n.t('quoteInfo.clientRate'), data: rfq.rfq_final_rate != null ? Number(rfq.rfq_final_rate).toFixed(4) : '-' },
    { header: i18n.t('quoteInfo.bankLastUpdate'), data: rfq.deal ? utils.formatTimeAndDate(rfq.quote_bank_time) : '-' }
  );

  return cols;
}

// Core PDF generation loop shared by both report types.
export async function generateReportPDF({ reportsData, buildItemColumns, fileName }) {
  const locale = localStorage.getItem('userLanguage') || 'en';
  const role = localStorage.getItem('userRole');
  const now = new Date();
  const formattedNow = formatTimestamp(now, locale);
  const saveDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const imgData = await loadLogoForPDF();

  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.height;
  const addHeader = buildAddHeader(doc, imgData);

  // Guard against autoTable firing didDrawPage multiple times for the same page.
  let lastHeaderPage = 0;
  function drawHeaderOnce() {
    const page = doc.internal.getNumberOfPages();
    if (page === lastHeaderPage) return;
    lastHeaderPage = page;
    addHeader();
  }

  drawHeaderOnce();
  let currentY = HEADER_HEIGHT;

  for (const item of reportsData) {
    const quotes = item.rfq_lastquote?.quote_list || [];
    const estimatedHeight = 15 + (quotes.length > 0 ? 10 + quotes.length * 5 : 0);

    if (currentY + estimatedHeight > pageHeight - MARGIN) {
      doc.addPage();
      currentY = HEADER_HEIGHT;
    }

    // --- Main table ---
    const { head, body, columnStyles } = columnsToAutoTableArgs(buildItemColumns(item, role));
    doc.autoTable({
      startY: currentY,
      margin: { top: HEADER_HEIGHT, left: MARGIN, right: MARGIN, bottom: MARGIN },
      styles: { fontSize: FONT_SIZE_CELL },
      headStyles: { fontSize: FONT_SIZE_HEAD, fillColor: [7, 7, 7] },
      head: [head],
      body: [body],
      columnStyles,
      didDrawPage: drawHeaderOnce,
    });
    currentY = doc.lastAutoTable.finalY;

    // --- Quotes sub-table ---
    if (quotes.length > 0) {
      // Build column definitions once for headers, then once per row for body data.
      // buildQuotesColumns({}, role) is a dummy call to extract headers without row data.
      const quoteCols = buildQuotesColumns({}, role, item);
      const { head: qHead, columnStyles: qStyles } = columnsToAutoTableArgs(quoteCols);
      const qBody = quotes.map(rfq => buildQuotesColumns(rfq, role, item).map(c => c.data));

      doc.autoTable({
        startY: currentY,
        margin: { top: HEADER_HEIGHT, left: MARGIN, right: MARGIN, bottom: MARGIN },
        styles: { fontSize: FONT_SIZE_CELL },
        headStyles: { fontSize: FONT_SIZE_HEAD, fillColor: [165, 165, 165] },
        head: [qHead],
        body: qBody,
        columnStyles: qStyles,
        didDrawPage: drawHeaderOnce,
      });
      currentY = doc.lastAutoTable.finalY;
    }

    currentY += REPORTS_GAP;
  }

  addPageNumbersAndTimestamp(doc, formattedNow);
  doc.save(`${fileName}_${saveDate}.pdf`);
}
